import { useApolloClient } from "@apollo/client";
import {
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useCallback, useState } from "react";
import { RiEditLine, RiRoadMapLine } from "react-icons/ri";
import { SchoolBoundaries } from "src/components/Boundary/schemas";
import { Card } from "src/components/Layout/Card";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { Glossary } from "src/components/Text/Glossary";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { EnrollmentPeriodIdProvider, useGlossary } from "src/hooks/useGlossary";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { GET_SCHOOL_DEFAULT_BIN_CONFIG } from "src/scenes/orgAdmin/match/graphql/queries";
import * as GQL from "src/types/graphql";
import {
  BULK_INSERT_GRADE_PROGRAMS,
  DELETE_BIN_BY_IDS,
  DELETE_GRADE_BY_PK,
} from "../graphql/mutations";
import { GET_GRADES_BY_ENROLLMENT_SCHOOL } from "../graphql/queries";
import { OrderedItem, orderedItemSortFn } from "../helpers";
import { SetBoundaryConnectionsDrawer } from "../SetBoundaryConnectionsDrawer";
import { BoundaryInfo } from "./BoundaryInfo";
import { Dropdown } from "./Dropdown";

type Mode = "View" | "Edit";

interface SchoolGradesCardProps {
  organizationId: string;
  schoolId: string;
  schoolName: string;
  enrollmentPeriodId: string;
  gradeConfigs: GQL.GetEnrollmentSchoolsAndConfigs_grade_config[];
  grades: GQL.GetEnrollmentSchoolsAndConfigs_school_grades[];
  boundaryMap: Immutable.Map<string, SchoolBoundaries>;
  refetchBoundaryMap?: () => Promise<void>;
}

export const SchoolGradesCard: React.FC<SchoolGradesCardProps> = ({
  organizationId,
  schoolId,
  schoolName,
  enrollmentPeriodId,
  gradeConfigs,
  grades,
  boundaryMap,
  refetchBoundaryMap,
}) => {
  const { glossary } = useGlossary({ enrollmentPeriodId });
  const toast = useAvelaToast();
  const [mode, setMode] = useState<Mode>("View");
  const {
    isOpen: isBoundariesDrawerOpen,
    onOpen: onBoundariesDrawerOpen,
    onClose: onBoundariesDrawerClose,
  } = useDisclosure();
  const [gradePrograms, setGradePrograms] = useState<
    GQL.GetGradesByEnrollmentSchool_school_by_pk_grades[]
  >([]);
  const [gradeInitialValues, setGradeInitialValues] = useState<OrderedItem[]>(
    []
  );
  const [gradeValues, setGradeValues] = useState<OrderedItem[]>([]);

  const { remoteData, refetch } = useRemoteDataQuery<
    GQL.GetGradesByEnrollmentSchool,
    GQL.GetGradesByEnrollmentSchoolVariables
  >(GET_GRADES_BY_ENROLLMENT_SCHOOL, {
    variables: {
      enrollment_period_id: enrollmentPeriodId,
      school_id: schoolId,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const gradePrograms = data.school_by_pk?.grades ?? [];
      const grades: OrderedItem[] = gradePrograms
        .map((gradeProgram) => ({
          id: gradeProgram.grade_config.id,
          label: gradeProgram.grade_config.label,
          order: gradeProgram.grade_config.order,
          binId: gradeProgram.bin_id,
          gradeId: gradeProgram.id,
        }))
        .sort(orderedItemSortFn);
      setGradePrograms(gradePrograms);
      setGradeInitialValues(grades);
      setGradeValues(grades);
    },
  });

  const apolloClient = useApolloClient();

  const [deleteGradeByPk, { remoteData: deleteGrade }] = useRemoteDataMutation<
    GQL.DeleteGradeByPk,
    GQL.DeleteGradeByPkVariables
  >(DELETE_GRADE_BY_PK);

  const [deleteBinByIds, { remoteData: deleteBin }] = useRemoteDataMutation<
    GQL.DeleteBinByIds,
    GQL.DeleteBinByIdsVariables
  >(DELETE_BIN_BY_IDS);

  const [bulkInsertGradePrograms, { remoteData: updateGrades }] =
    useRemoteDataMutation<
      GQL.BulkInsertGradePrograms,
      GQL.BulkInsertGradeProgramsVariables
    >(BULK_INSERT_GRADE_PROGRAMS);

  const isLoading =
    deleteGrade.isLoading() ||
    updateGrades.isLoading() ||
    deleteBin.isLoading() ||
    remoteData.isLoading();

  const handleSubmit = useCallback(async () => {
    const { data: schoolDefaultBinConfigData } = await apolloClient.query<
      GQL.GetSchoolDefaultBinConfig,
      GQL.GetSchoolDefaultBinConfigVariables
    >({
      query: GET_SCHOOL_DEFAULT_BIN_CONFIG,
      variables: { schoolId, enrollmentPeriodId },
    });

    const gradesToAttach = gradeValues?.filter((value) =>
      gradeInitialValues.every((initVal) => value.id !== initVal.id)
    );
    const gradesToDetach = gradeInitialValues.filter((initVal) =>
      gradeValues?.every((value) => value.id !== initVal.id)
    );
    const formattedGradesToAttach: GQL.grade_insert_input[] =
      gradesToAttach?.map((gradeToAttach) => ({
        school_id: schoolId,
        enrollment_period_id: enrollmentPeriodId,
        grade_config_id: gradeToAttach.id,
        bin: {
          data: {
            enrollment_period_id: enrollmentPeriodId,
            bin_config_id:
              schoolDefaultBinConfigData.school_default_bin_config[0]
                ?.bin_config_id ?? null,
          },
        },
      }));

    try {
      if (formattedGradesToAttach?.length) {
        await bulkInsertGradePrograms({
          variables: {
            grade_programs: formattedGradesToAttach,
          },
        });
      }

      if (gradesToDetach?.length) {
        await Promise.all(
          gradesToDetach.map(async (gradeToDetach) => {
            console.log("gradeToDetach", gradeToDetach);
            if (!gradeToDetach.gradeId) return;

            await deleteGradeByPk({
              variables: {
                id: gradeToDetach.gradeId,
              },
            });
            if (gradeToDetach.binId) {
              try {
                await deleteBinByIds({
                  variables: {
                    bin_ids: [gradeToDetach.binId],
                  },
                });
              } catch (err) {
                // ignore error: bin may still be linked to other grades
              }
            }
          })
        );
      }

      await refetch();
      toast({
        id: "update-grades-success",
        title: glossary`School updated`,
        isClosable: true,
        status: "success",
        position: "bottom-right",
      });
      setMode("View");
    } catch (err) {
      toast({
        id: "update-grades-error",
        title: glossary`Error updating school`,
        description:
          "Please try again later or report the problem to our support team.",
        isClosable: true,
        status: "error",
      });
    }
  }, [
    apolloClient,
    schoolId,
    gradeValues,
    gradeInitialValues,
    enrollmentPeriodId,
    refetch,
    toast,
    glossary,
    bulkInsertGradePrograms,
    deleteGradeByPk,
    deleteBinByIds,
  ]);

  const flags = useFlags(["show-school-boundaries", "boundary-management"]);

  return (
    <EnrollmentPeriodIdProvider enrollmentPeriodId={enrollmentPeriodId}>
      <Card
        display="flex"
        flexDirection="column"
        showBorder
        gap={4}
        padding={6}
        width="100%"
      >
        <Flex justifyContent="space-between">
          <Heading fontSize="lg" mb={4}>
            <Text as="span" color="blackAlpha.800">
              {schoolName}{" "}
            </Text>
            <Text as="span" color="gray.500">
              | {schoolId}
            </Text>
          </Heading>
          <Flex gap={2}>
            {mode === "View" && (
              <Button
                colorScheme="gray"
                variant="outline"
                leftIcon={<RiEditLine />}
                onClick={() => setMode("Edit")}
              >
                <Glossary>Edit school</Glossary>
              </Button>
            )}
            {mode === "Edit" && flags["boundary-management"].enabled && (
              <Button
                leftIcon={<RiRoadMapLine />}
                colorScheme="gray"
                variant="outline"
                onClick={onBoundariesDrawerOpen}
              >
                Set boundaries
              </Button>
            )}
          </Flex>
        </Flex>
        {isLoading ? (
          <Spinner color="primary.500" />
        ) : mode === "View" ? (
          <Flex direction="column" gap={2}>
            <Text fontSize="xs" color="gray.800">
              Grades
            </Text>
            {gradePrograms.length === 0 && (
              <Text as="i" color="blackAlpha.700">
                No grades
              </Text>
            )}
            <Text color="blackAlpha.700">
              {gradeInitialValues.map((grade) => grade.label).join(", ")}
            </Text>
            {flags["boundary-management"].enabled && (
              <BoundaryInfo
                boundaryMap={boundaryMap}
                grades={grades}
                schoolId={schoolId}
              />
            )}
          </Flex>
        ) : (
          <>
            <Card
              display="flex"
              flexDirection="column"
              showBorder
              padding={6}
              width="100%"
            >
              <Dropdown
                itemType="Grades"
                options={gradeConfigs}
                initialValues={gradeInitialValues}
                onChange={(grades: OrderedItem[]) => {
                  setGradeValues(grades.sort(orderedItemSortFn));
                }}
              />
            </Card>
            <Flex gap="3" justifyContent="right" paddingY="3">
              <Button
                variant="ghost"
                colorScheme="gray"
                onClick={() => {
                  setMode("View");
                  setGradeValues(gradeInitialValues);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmit}>
                <Glossary>Update school</Glossary>
              </Button>
            </Flex>
          </>
        )}

        <SetBoundaryConnectionsDrawer
          isOpen={isBoundariesDrawerOpen}
          onClose={onBoundariesDrawerClose}
          organizationId={organizationId}
          enrollmentPeriodId={enrollmentPeriodId}
          schoolId={schoolId}
          schoolName={schoolName}
          grades={grades}
          refetchBoundaryMap={refetchBoundaryMap}
        />
      </Card>
    </EnrollmentPeriodIdProvider>
  );
};
